/*
  Geometry for the bonsai: builds every dot once (position, size, brightness, the
  moment it appears, and for surfaces that can turn away, a normal) and returns
  them sorted far to near. Nothing here draws.

  The tree is a twisting trunk with flared surface roots, four limbs, and a canopy
  of six foliage pads. Each pad is a dotted heightfield on one shared lattice (the
  same construction as the homepage loss landscape), with small bumps for leaf
  clusters, a noisy outline for rough edges, and a ragged skirt underneath for
  thickness. Parts of a pad tucked inside a neighbouring pad are dropped, so the
  canopy reads as one continuous surface. Below it: a shallow pot with feet, soil,
  and a faint line of dots for the table.
*/

// Canvas and camera. World units: the tree is about 2 tall; y is up and +z points away from the viewer.
export const W = 520;
export const H = 210;
export const PITCH = 0.42;              // camera looks slightly down on the tree
export const DIST = 6;
export const SCALE = 68;                // px per world unit
export const CX = 260;
export const CY = 170;                  // screen y of the soil line

// The view swings back and forth like the homepage figure (same amplitude and period).
export const YAW = 0.15;                // rad
export const YAW_PERIOD = 30;        // s

const PI = Math.PI, S = Math.sin, C = Math.cos;
const SP = 0.06;                        // dot spacing on the trunk and limbs
const LEAF = 0.044;                     // lattice spacing of the foliage
const T0 = 0.7, TD = 2.2, BD = 1.1;     // trunk starts, trunk growth time, limb growth time (s)

export const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
export const smooth = (a, b, x) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
const dotp = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const norm = (v) => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
const VIEW = [0, S(PITCH), -C(PITCH)];               // surface -> camera, at the centre of the swing
const LIGHT = norm([-0.45, 0.75, -0.5]);             // upper left, in front
const BACK = -0.65;                                   // surfaces facing further away than this never come into view

// Smooth, cheap 2D noise in roughly [-1, 1]; `s` picks a different pattern.
const fbm = (x, z, s) => 0.5 * S(3.1 * x + 1.7 * s) * C(2.9 * z - 0.6 * s)
  + 0.3 * S(6.3 * x - 2.1 + s) * S(5.7 * z + 1.1 * s)
  + 0.2 * C(11.7 * x + 0.4 * s) * S(12.9 * z - 1.3);

function rng(seed) {
  let a = seed >>> 0;
  return () => { a = (a + 0x6D2B79F5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

// Catmull-Rom centreline, resampled by arc length
function makeCurve(ctrl) {
  const P = [ctrl[0].map((v, i) => 2 * v - ctrl[1][i]), ...ctrl, ctrl[ctrl.length - 1].map((v, i) => 2 * v - ctrl[ctrl.length - 2][i])];
  const pts = [];
  for (let i = 1; i < P.length - 2; i++) for (let k = 0; k < 24; k++) {
    const t = k / 24, t2 = t * t, t3 = t2 * t;
    pts.push([0, 1, 2].map((d) => 0.5 * ((2 * P[i][d]) + (-P[i - 1][d] + P[i + 1][d]) * t + (2 * P[i - 1][d] - 5 * P[i][d] + 4 * P[i + 1][d] - P[i + 2][d]) * t2 + (-P[i - 1][d] + 3 * P[i][d] - 3 * P[i + 1][d] + P[i + 2][d]) * t3)));
  }
  pts.push(ctrl[ctrl.length - 1]);
  const L = [0];
  for (let i = 1; i < pts.length; i++) L.push(L[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1], pts[i][2] - pts[i - 1][2]));
  const total = L[L.length - 1];
  const at = (u) => {
    const s = clamp(u, 0, 1) * total; let i = 1;
    while (i < L.length - 1 && L[i] < s) i++;
    const f = (s - L[i - 1]) / Math.max(1e-9, L[i] - L[i - 1]);
    const p = [0, 1, 2].map((d) => pts[i - 1][d] + (pts[i][d] - pts[i - 1][d]) * f);
    return { p, t: norm([0, 1, 2].map((d) => pts[i][d] - pts[i - 1][d])) };
  };
  return { total, at };
}

const TRUNK = makeCurve([[0.06, -0.02, 0], [-0.24, 0.26, 0.05], [-0.16, 0.56, 0], [0.22, 0.82, -0.03], [0.08, 1.1, 0], [-0.06, 1.34, 0]]);
const trunkR = (u) => 0.11 * Math.pow(1 - u, 0.7) * (1 + 0.9 * Math.exp(-u / 0.07)) + 0.035;

// Surface roots: where each one ends on the soil
const ROOTS = [[-0.6, 0.12], [-0.32, -0.25], [0.55, 0.05], [0.3, -0.22], [0.1, 0.3]];

// s0: where the limb leaves the trunk; pts: the rest of its path
const LIMBS = [
  { s0: 0.78, pts: [[-0.42, 1.16, 0.05], [-0.9, 1.12, 0], [-1.32, 1.2, -0.05]], rb: 0.05 },
  { s0: 0.66, pts: [[0.5, 1.06, 0.05], [0.95, 1.1, 0.1], [1.36, 0.96, 0.1]], rb: 0.05 },
  { s0: 0.95, pts: [[0.0, 1.5, 0], [0.06, 1.62, 0]], rb: 0.04 },
  { s0: 0.86, pts: [[0.3, 1.4, -0.2], [0.46, 1.46, -0.32]], rb: 0.034 },
];

// Foliage pads. c: base centre; rx, rz: footprint radii; h: dome height; limb: which limb feeds it; s: noise seed.
const PADS = [
  { c: [-1.08, 1.3, -0.02], rx: 0.66, rz: 0.46, h: 0.2, limb: 0, s: 1.3 },
  { c: [-0.05, 1.64, 0], rx: 0.84, rz: 0.52, h: 0.26, limb: 2, s: 2.7 },
  { c: [1.0, 1.25, 0.08], rx: 0.62, rz: 0.46, h: 0.2, limb: 1, s: 4.1 },
  { c: [1.44, 0.96, 0.12], rx: 0.32, rz: 0.3, h: 0.15, limb: 1, s: 5.6 },
  { c: [0.44, 1.48, -0.3], rx: 0.52, rz: 0.36, h: 0.18, limb: 3, s: 6.9 },
  { c: [-0.58, 1.47, 0.12], rx: 0.42, rz: 0.34, h: 0.16, limb: 0, s: 8.2 },
];

// Normalised distance from a pad's centre; the outline (q = 1) is pushed in and out by noise for rough edges.
function padQ(P, x, z) {
  const dx = (x - P.c[0]) / P.rx, dz = (z - P.c[2]) / P.rz, th = Math.atan2(dz, dx);
  const edge = 1 + 0.11 * S(5 * th + P.s) + 0.07 * S(11 * th + 2 * P.s) + 0.06 * S(19 * th + 3 * P.s) + 0.1 * fbm(2.2 * x, 2.2 * z, P.s);
  return Math.hypot(dx, dz) / edge;
}
// Top of the pad: a dome with leaf-cluster bumps on two scales.
function padTop(P, x, z) {
  const q = padQ(P, x, z);
  return P.c[1] + P.h * Math.sqrt(Math.max(0, 1 - q * q)) + 0.045 * fbm(3.4 * x, 3.4 * z, P.s + 1) + 0.02 * fbm(8 * x, 8 * z, P.s + 2);
}
// Ragged underside of the pad.
const padBottom = (P, x, z) => P.c[1] - 0.07 - 0.04 * (0.5 + 0.5 * fbm(5 * x, 5 * z, P.s + 4));
// True when a point of pad i sits inside another pad.
const hidden = (i, x, y, z) => PADS.some((Q, j) => j !== i && padQ(Q, x, z) < 1 && y < padTop(Q, x, z) - 0.01 && y > padBottom(Q, x, z) - 0.02);

export function buildScene() {
  const R = rng(7), dots = [];
  const push = (o) => dots.push(Object.assign({ fx: o.x, fy: o.y, fz: o.z, d: 0.4, out: 0, p3: R() * 6.28, p4: R() * 6.28, w3: 0.5 + R() * 1.3, w4: 0.5 + R() * 1.1 }, o));
  const grow = (t0, dur, u) => t0 + dur * (1 - Math.sqrt(1 - clamp(u, 0, 1)));   // matches an ease-out growth front
  let end = 0;

  // ---- tubes (trunk, roots, limbs) ----
  function tube(curve, radiusFn, t0, dur, baseA, rowStep, twist) {
    let N1 = null;
    const rows = Math.max(2, Math.round(curve.total / rowStep));
    for (let j = 0; j <= rows; j++) {
      const u = j / rows, { p, t } = curve.at(u);
      if (!N1) { N1 = norm(cross(t, [0, 0, 1])); if (Math.hypot(...N1) < 0.5) N1 = norm(cross(t, [1, 0, 0])); }
      const dtn = dotp(N1, t); N1 = norm([N1[0] - t[0] * dtn, N1[1] - t[1] * dtn, N1[2] - t[2] * dtn]);
      const N2 = cross(t, N1);
      const r = radiusFn(u) * (1 + 0.06 * S(u * 37 + 1.3) + 0.04 * S(u * 91));
      const n = Math.max(4, Math.round(2 * PI * r / SP));
      for (let i = 0; i < n; i++) {
        const ph = (i + (j % 2) * 0.5) / n * 2 * PI + u * twist;
        const nv = [C(ph) * N1[0] + S(ph) * N2[0], C(ph) * N1[1] + S(ph) * N2[1], C(ph) * N1[2] + S(ph) * N2[2]];
        if (dotp(nv, VIEW) < BACK) continue;
        const sh = 1 - clamp(dotp(nv, LIGHT), 0, 1);
        push({ x: p[0] + nv[0] * r, y: p[1] + nv[1] * r, z: p[2] + nv[2] * r, n: nv, r: 0.9 + 0.45 * sh, a: clamp(baseA + (1 - baseA) * 0.8 * sh + 0.12 * S(ph * 3 + u * 28), 0.3, 1), b: grow(t0, dur, u) });
      }
    }
  }
  tube(TRUNK, trunkR, T0, TD, 0.6, 0.045, 6.5);
  ROOTS.forEach(([x, z]) => tube(makeCurve([[0.04, 0.08, 0], [x * 0.5, 0, z * 0.5], [x, -0.02, z]]), (u) => 0.06 * (1 - 0.8 * u) + 0.01, 0.5, 0.6, 0.65, 0.045, 1));
  const limbs = LIMBS.map((l) => {
    const t0 = grow(T0, TD, l.s0), curve = makeCurve([TRUNK.at(l.s0).p, ...l.pts]);
    tube(curve, (u) => l.rb * (1 - 0.6 * u) + 0.012, t0, BD, 0.72, 0.05, 2);
    return { curve, t0 };
  });

  // ---- foliage pads ----
  PADS.forEach((P, i) => {
    const tb = limbs[P.limb].t0 + 0.8;

    // One foliage dot. Shadowed and edge dots are denser and larger; each blooms out from the pad's centre.
    const leaf = (x, y, z, nrm, q, edge) => {
      const sh = 1 - clamp(dotp(nrm, LIGHT), 0, 1), tex = fbm(6 * x, 6 * z, P.s + 3);
      const b = tb + 0.9 * Math.pow(clamp(q, 0, 1), 0.85) + R() * 0.15;
      end = Math.max(end, b + 0.7);
      push({
        x, y, z, n: edge ? nrm : undefined,
        fx: P.c[0] + (x - P.c[0]) * 0.3, fy: P.c[1] - 0.05, fz: P.c[2] + (z - P.c[2]) * 0.3,
        r: 0.8 + 0.35 * sh + 0.12 * tex,
        a: clamp(0.2 + 0.5 * sh + 0.1 * tex + (edge ? 0.12 : 0) + 0.1 * smooth(0.6, 1, q), 0.14, 0.86),
        b, d: 0.7, out: 0.1 + 0.9 * smooth(0.55, 1, q),
      });
    };

    const gz = LEAF * 0.9, e = 0.01;
    const x0 = Math.round((P.c[0] - P.rx * 1.45) / LEAF), x1 = Math.round((P.c[0] + P.rx * 1.45) / LEAF);
    const z0 = Math.round((P.c[2] - P.rz * 1.45) / gz), z1 = Math.round((P.c[2] + P.rz * 1.45) / gz);
    for (let xi = x0; xi <= x1; xi++) for (let zi = z0; zi <= z1; zi++) {
      const x = xi * LEAF + (zi % 2) * LEAF * 0.5, z = zi * gz, q = padQ(P, x, z);
      if (q >= 1) continue;
      const y = padTop(P, x, z);
      if (!hidden(i, x, y, z)) {
        const nrm = norm([-(padTop(P, x + e, z) - padTop(P, x - e, z)) / (2 * e), 1, -(padTop(P, x, z + e) - padTop(P, x, z - e)) / (2 * e)]);
        leaf(x, y, z, nrm, q, false);
      }
      // Lattice points on the outline get a column of dots down to the ragged underside.
      const onEdge = [[LEAF, 0], [-LEAF, 0], [0, gz], [0, -gz]].some(([dx, dz]) => padQ(P, x + dx, z + dz) >= 1);
      if (!onEdge) continue;
      const out = norm([padQ(P, x + e, z) - padQ(P, x - e, z), 0.15, padQ(P, x, z + e) - padQ(P, x, z - e)]);
      if (dotp(out, VIEW) < BACK) continue;
      const yb = padBottom(P, x, z);
      for (let yy = y - 0.04; yy > yb; yy -= 0.04) if (!hidden(i, x, yy, z)) leaf(x, yy, z, out, 1, true);
    }
  });

  // ---- pot, soil, table ----
  const hw = 0.88, hz = 0.36;
  for (let row = 0; row <= 4; row++) {                                  // front face, rim row first
    const y = -0.04 - row * 0.05, w = hw - 0.05 * row / 4, cols = Math.round(2 * w / 0.055);
    for (let i = 0; i <= cols; i++) push({ x: -w + 2 * w * i / cols, y, z: -hz, r: row === 0 ? 1.15 : 0.95, a: row === 0 ? 1 : 0.85 - 0.1 * row / 4, b: 0.05 + 0.3 * row / 4 + R() * 0.2 });
  }
  for (let i = 0; i <= 32; i++) {                                       // front and back rim
    const x = -hw + 2 * hw * i / 32;
    push({ x, y: -0.01, z: -hz - 0.02, r: 1.1, a: 0.95, b: 0.05 + R() * 0.2 });
    push({ x, y: -0.01, z: hz, r: 0.85, a: 0.45, b: 0.05 + R() * 0.2 });
  }
  for (let i = 1; i < 12; i++) for (const sgn of [-1, 1]) push({ x: sgn * hw, y: -0.01, z: -hz + 2 * hz * i / 12, r: 1, a: 0.7, b: 0.05 + R() * 0.2 });
  for (const fx of [-0.62, 0.62]) for (let i = 0; i < 4; i++) for (let k = 0; k < 2; k++) push({ x: fx - 0.08 + 0.055 * i, y: -0.27 - 0.045 * k, z: -hz + 0.04, r: 0.95, a: 0.9, b: 0.3 + R() * 0.2 });
  for (let i = 0; i < 24; i++) for (let k = 0; k < 9; k++) {           // soil, mounded around the trunk
    const x = -hw * 0.95 + 1.9 * hw * (i + R() * 0.7) / 24, z = -hz * 0.9 + 1.8 * hz * (k + R() * 0.7) / 9, mound = Math.exp(-(x * x) / 0.25 - (z * z) / 0.1);
    push({ x, y: -0.03 + 0.07 * mound, z, r: 0.65, a: 0.3 + 0.2 * R() + 0.2 * mound, b: 0.25 + R() * 0.4 });
  }
  for (let i = 0; i <= 64; i++) { const x = -2.2 + 4.4 * i / 64, ex = Math.abs(x) / 2.2; push({ x, y: -0.36, z: -0.05, r: 0.8, a: 0.55 * (1 - ex * ex), b: 0.2 + 0.6 * ex }); }

  // painter's order: far to near
  const zc = (d) => d.z * C(PITCH) - d.y * S(PITCH);
  dots.sort((a, b) => zc(b) - zc(a));
  // `end` is when the last foliage dot has finished blooming
  return { dots, end };
}